import React, { useState, forwardRef, useImperativeHandle } from "react";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ApiClient from "../../../services/api-client"
import useStore from "../../../hooks/useStore";
import { CustomConfirmAlert } from '../../custom-confirm-alert';

const EMPTY_ROW = { name: '', amountValue: '', percent: false, subtracted: false };

const isValid = (row) =>
    row.name != null && row.name !== '' && row.amountValue != null && row.amountValue !== '';

const hasDuplicateName = (rows, index) => {
    const name = rows[index]?.name?.trim().toLowerCase();
    if (!name) return false;
    return rows.some((r, i) => i !== index && r.name?.trim().toLowerCase() === name);
};

const CompanyDeductionsGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
    const [touched, setTouched] = useState([]);

    const hasError = rows.some((row, i) => touched[i] && (!isValid(row) || hasDuplicateName(rows, i)));

    useImperativeHandle(ref, () => ({
        transformData() {},
        getData() {
            return rows.filter(isValid);
        },
        hasError() {
            return hasError;
        },
        setData(data) {
            setRows(data && data.length ? [...data, { ...EMPTY_ROW }] : [{ ...EMPTY_ROW }]);
            setTouched([]);
        }
    }));

    const updateRow = (index, field, value) => {
        const next = rows.map((r, i) => i === index ? { ...r, [field]: value } : r);
        // Auto-append empty row when the last row gets its first text input
        if (index === rows.length - 1 && value !== '' && value !== false) {
            next.push({ ...EMPTY_ROW });
        }
        setRows(next);
        setTouched(prev => { const t = [...prev]; t[index] = true; return t; });
    };

    const deleteRow = (index) => {
        const row = rows[index];
        const companyId = useStore.getState().selectedCompany.id;

        const removeLocally = () => {
            setRows(prev => {
                const next = prev.filter((_, i) => i !== index);
                return next.length ? next : [{ ...EMPTY_ROW }];
            });
            setTouched(prev => prev.filter((_, i) => i !== index));
        };

        CustomConfirmAlert({
            title: t("submitConfirm"),
            message: t("areYouSure"),
            buttons: [
                {
                    label: t("Yes"),
                    onClick: () => {
                        if (row.id != null) {
                            ApiClient.deleteCompanyDeduction(companyId, row.id).then(
                                () => { toast.success(t("deleteSucceded")); removeLocally(); },
                                error => toast.error("Error: " + error.data)
                            );
                        } else {
                            removeLocally();
                        }
                    }
                },
                { label: t("No") }
            ]
        });
    };

    const addRow = () => {
        setRows(prev => [...prev, { ...EMPTY_ROW }]);
    };

    const isLastEmpty = (index) => index === rows.length - 1 && !isValid(rows[index]);
    const isDuplicate = (index) => touched[index] && hasDuplicateName(rows, index) && !isLastEmpty(index);
    const fieldError = (index, field) => touched[index] && !rows[index][field] && !isLastEmpty(index);

    return (
        <div className="deduction-table-wrapper">
            <table className="table deduction-table">
                <thead>
                    <tr>
                        <th style={{ width: '40%' }}>{t("Name")}</th>
                        <th className="text-center" style={{ width: '15%' }}>{t("percent")}</th>
                        <th className="text-center" style={{ width: '15%' }}>{t("isSubtracted")}</th>
                        <th style={{ width: '20%' }}>{t("Amount")}</th>
                        <th style={{ width: '10%' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className={(touched[i] && !isValid(row) && !isLastEmpty(i)) || isDuplicate(i) ? 'deduction-row-error' : 'deduction-row'}>
                            <td>
                                <input
                                    className={`form-control form-control-sm${fieldError(i, 'name') || isDuplicate(i) ? ' is-invalid' : ''}`}
                                    value={row.name || ''}
                                    onChange={e => updateRow(i, 'name', e.target.value)}
                                    placeholder={t("Name")}
                                />
                                {isDuplicate(i) && (
                                    <div className="invalid-feedback d-block">{t("duplicateName")}</div>
                                )}
                            </td>
                            <td className="text-center align-middle">
                                <div className="form-check form-switch d-flex justify-content-center mb-0">
                                    <input
                                        className="form-check-input deduction-switch"
                                        type="checkbox"
                                        checked={!!row.percent}
                                        onChange={e => updateRow(i, 'percent', e.target.checked)}
                                    />
                                </div>
                            </td>
                            <td className="text-center align-middle">
                                <div className="form-check form-switch d-flex justify-content-center mb-0">
                                    <input
                                        className="form-check-input deduction-switch"
                                        type="checkbox"
                                        checked={!!row.subtracted}
                                        onChange={e => updateRow(i, 'subtracted', e.target.checked)}
                                    />
                                </div>
                            </td>
                            <td>
                                <input
                                    className={`form-control form-control-sm${fieldError(i, 'amountValue') ? ' is-invalid' : ''}`}
                                    value={row.amountValue || ''}
                                    onChange={e => updateRow(i, 'amountValue', e.target.value)}
                                    placeholder="0.0"
                                    type="number"
                                    step="0.1"
                                    min="0"
                                />
                            </td>
                            <td className="text-center align-middle">
                                {!isLastEmpty(i) && (
                                    <button
                                        type="button"
                                        className="btn btn-sm deduction-delete-btn"
                                        onClick={() => deleteRow(i)}
                                        title={t("Delete")}
                                    >
                                        <i className="fa fa-trash-o"></i>
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <button type="button" className="btn btn-sm deduction-add-btn" onClick={addRow}>
                <i className="fa fa-plus" style={{ marginRight: 6 }}></i>
                {t("Add")}
            </button>
        </div>
    );
});

export default CompanyDeductionsGrid;
